import styled from '@emotion/styled'
import Chip from '@mui/material/Chip'
import Icon from '@mui/material/Icon'
import Link from '@mui/material/Link'
import Typography from '@mui/material/Typography'
import { isEmpty, path } from 'ramda'
import React from 'react'
import { useDropzone } from 'react-dropzone'

import type { UploadError, UploadFilePayload, UploadFileState } from '../../types'

type Props = {
  error: UploadError
  file: UploadFileState
  fileName: string
  onAccept: (payload: { error?: UploadError; file: UploadFilePayload; fileName: string }) => void
  onDelete: () => void
}

const ACCEPTED_EXTENSIONS = ['xlsx', 'csv']

const getUploadErrorMessage = (error: UploadError): React.ReactNode => {
  if (typeof error === 'object' && error && 'message' in error) {
    return (error as { message?: React.ReactNode }).message
  }
  return undefined
}

const ContactsUploadInitContent: React.FC<Props> = ({ error, file, fileName, onAccept, onDelete }) => {
  const hasError = !isEmpty(error)

  const onDrop = React.useCallback(
    ([fileDropped]: File[]) => {
      const data = new FormData()
      const filename = fileDropped?.name ?? ''
      const ext = filename.split('.').pop() ?? ''
      if (filename && ACCEPTED_EXTENSIONS.indexOf(ext) > -1) {
        data.append('file', fileDropped)
        return onAccept({
          file: data,
          fileName: fileDropped.name,
          error: {},
        })
      }
      return onAccept({
        file: {},
        fileName: '',
        error: {
          message: (
            <span>
              File type is invalid.
              <br />
              Currently, we support Excel documents with an extension of <b>.xlsx</b> or <b>.csv</b>.
            </span>
          ),
        },
      })
    },
    [onAccept],
  )

  const { getRootProps, getInputProps } = useDropzone({
    onDrop,
    multiple: false,
    accept: {
      'text/csv': ['.csv'],
      'application/vnd.ms-excel': ['.csv'],
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
    },
    noClick: false,
    noKeyboard: false,
  })

  return (
    <Wrap>
      <DropArea {...getRootProps()} $hasError={hasError}>
        <input {...getInputProps()} />
        <DropIcon>cloud_upload</DropIcon>
        {file ? (
          <DropText>
            <Typography gutterBottom={true}>{isEmpty(error) ? 'File added' : getUploadErrorMessage(error)}</Typography>
            {fileName && <Chip label={fileName} onDelete={onDelete} />}
          </DropText>
        ) : (
          <DropText>
            <Typography variant="h6">Drop a file here, or click to select one</Typography>
            <Typography variant="caption" color="textSecondary">
              Upload a csv or xlsx with one row per contact.
              <br />
              Your columns must have names/column headings. You can name your columns anything you want, and we'll add
              them.
              <br />
              <strong>One of the columns must contain your contacts' phone numbers.</strong>
            </Typography>
          </DropText>
        )}
      </DropArea>
      <TemplateLink variant="h6" href="/static/contacts-upload-template.xlsx" target="_blank">
        Download Template
      </TemplateLink>
    </Wrap>
  )
}

const Wrap = styled('div')({
  position: 'relative',
  overflow: 'hidden',
  width: '100%',
  height: '100%',
})

const DropArea = styled('div')<{ $hasError: boolean }>((props) => ({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  width: '100%',
  height: '200px',
  backgroundColor: path(['palette', 'grey', '100'], props.theme as any),
  color: path(['palette', 'common', 'white'], props.theme as any),
  borderRadius: '4px',
  border: `2px dashed ${path(['palette', 'grey', '300'], props.theme as any)}`,
  cursor: 'pointer',
  marginTop: '1.5rem',
  borderColor: props.$hasError ? path(['palette', 'error', 'main'], props.theme as any) : undefined,
  position: 'relative',
}))

const DropIcon = styled(Icon)({
  position: 'absolute',
  fontSize: '7rem',
})

const DropText = styled('div')({
  zIndex: 1,
  textAlign: 'center',
  color: 'rgba(0, 0, 0, 0.87)',
})

const TemplateLink = styled(Link)({
  marginTop: '0.75rem',
  display: 'inline-block',
})

export default ContactsUploadInitContent
